import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { leagueApi, tournamentApi } from "@bw/shared/api";
import { Link as RouterLink } from "react-router-dom";
import { queryKeys } from "../../../common/query/queryClient";
import type { FacilityCompetitionsProps } from "../competition.types";

/** Two bounded owner reads for this venue, never a facility-directory fanout. */
export function FacilityCompetitions({
  facilityId,
}: FacilityCompetitionsProps) {
  const tournaments = useQuery({
    queryKey: [...queryKeys.tournaments.all, "facility-presence", facilityId],
    queryFn: () =>
      tournamentApi.browse({ facility_id: facilityId, limit: 1, offset: 0 }),
  });
  const leagues = useQuery({
    queryKey: [...queryKeys.leagues.all, "facility-presence", facilityId],
    queryFn: () =>
      leagueApi.discover({ facility_id: facilityId, limit: 1, offset: 0 }),
  });
  const hasTournaments =
    tournaments.isSuccess &&
    tournaments.data.items.some((item) => item.facility_id === facilityId);
  const hasLeagues =
    leagues.isSuccess &&
    leagues.data.items.some((item) => item.facility_id === facilityId);
  const error = tournaments.isError || leagues.isError;
  const failure = error ? (
    <Alert
      severity="warning"
      action={
        <Button
          onClick={() =>
            void Promise.all([tournaments.refetch(), leagues.refetch()])
          }
        >
          Retry
        </Button>
      }
    >
      Some competition information is unavailable.
    </Alert>
  ) : null;
  if (!hasTournaments && !hasLeagues) return failure;
  const query = new URLSearchParams({ facility_id: facilityId });
  return (
    <Box
      component="section"
      aria-labelledby="facility-competition-title"
    >
      <Typography
        component="h2"
        id="facility-competition-title"
        sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}
      >
        Coming up here
      </Typography>
      <List disablePadding>
        {hasTournaments ? (
          <ListItem disablePadding divider>
            <ListItemButton
              component={RouterLink}
              to={`/compete/tournaments?${query}`}
            >
              <ListItemText primary="Tournaments" />
            </ListItemButton>
          </ListItem>
        ) : null}
        {hasLeagues ? (
          <ListItem disablePadding divider>
            <ListItemButton
              component={RouterLink}
              to={`/compete/leagues?${query}`}
            >
              <ListItemText primary="Leagues" />
            </ListItemButton>
          </ListItem>
        ) : null}
      </List>
      {failure}
    </Box>
  );
}
